import { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Loader2, HardDriveDownload } from 'lucide-react';
import { dataEngineApi } from '../services/dataEngineApi';
import CompanyDrawer from '../components/sync/CompanyDrawer';
import GLSyncTab from '../components/sync/GLSyncTab';
import EntitySyncTab from '../components/sync/EntitySyncTab';
import ReconcileTab from '../components/sync/ReconcileTab';
import StatusBadge from '../components/sync/StatusBadge';
import BulkReconcileTab from '../components/sync/BulkReconcileTab';

/* ─── Helpers ──────────────────────────────────────────── */
const formatDate = (d) => {
  if (!d) return '—';
  try {
    const date = new Date(d);
    const now = new Date();
    const diffH = (now - date) / 3600000;
    if (diffH < 1) return `${Math.round(diffH * 60)}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    if (diffH < 168) return `${Math.round(diffH / 24)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

/* ─── Main Page ────────────────────────────────────────── */
export default function DataSyncPage() {
  const [selectedRfIds, setSelectedRfIds] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [pageTab, setPageTab] = useState('companies');
  const [syncing, setSyncing] = useState(false);
  const PAGE_SIZE = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [realmsResp, companiesResp] = await Promise.all([
        dataEngineApi.getRealms(),
        dataEngineApi.getEntitySyncCompanies(page, PAGE_SIZE, search),
      ]);
      const realms = realmsResp.data || [];
      const compData = companiesResp.data || {};
      const compList = compData.content || [];

      setTotalElements(compData.totalElements || 0);
      setTotalPages(compData.totalPages || 0);

      const merged = compList.map(comp => {
        const realm = realms.find(r => r.rf_id === comp.realmId) || {};
        return {
          id: comp.id,
          rf_id: comp.realmId,
          display_name: comp.companyName,
          source_type: 'QUICKBOOKS',
          is_active: comp.isActive,
          row_count: realm.row_count || 0,
          last_synced_at: realm.last_synced_at,
          file_size_bytes: realm.file_size_bytes || 0,
        };
      });

      setCompanies(merged);
    } catch (err) {
      console.error('Failed to load:', err);
    }
    setLoading(false);
  }, [page, search]);

  const handleSyncAll = async () => {
    const count = selectedRfIds.length;
    const msg = count
      ? `Sync ${count} selected ${count === 1 ? 'company' : 'companies'}?`
      : 'Sync ALL active companies? This may take a while.';
    if (!window.confirm(`${msg} This will fetch GL data and run reconciliation.`)) return;
    setSyncing(true);
    try {
      const result = await dataEngineApi.triggerSyncAll(count ? selectedRfIds : null);
      alert(`Triggered sync for ${result.triggered} companies. Run ID: ${result.reconciliation_run_id}`);
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const toggleSelect = (rfId) => {
    setSelectedRfIds(prev =>
      prev.includes(rfId) ? prev.filter(id => id !== rfId) : [...prev, rfId]
    );
  };

  const toggleSelectAll = () => {
    const allRfIds = companies.map(c => c.rf_id);
    const allSelected = allRfIds.every(id => selectedRfIds.includes(id));
    if (allSelected) {
      setSelectedRfIds(prev => prev.filter(id => !allRfIds.includes(id)));
    } else {
      setSelectedRfIds(prev => [...new Set([...prev, ...allRfIds])]);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [search]);

  return (
    <div className="h-full overflow-y-auto bg-gray-950 text-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <HardDriveDownload className="h-5 w-5 text-blue-400" />
              </div>
              Sync
            </h1>
            <p className="text-sm text-gray-500 mt-2">Manage QuickBooks data synchronization across your companies</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncAll}
              disabled={syncing}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 text-white rounded transition-colors"
            >
              {syncing ? 'Syncing...' : selectedRfIds.length ? `Sync (${selectedRfIds.length})` : 'Sync All'}
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 bg-gray-800/60 hover:bg-gray-800 rounded-lg border border-gray-700/50 transition-all hover:border-gray-600"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Page Tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-800 pb-2">
          {[
            { key: 'companies', label: 'Companies' },
            { key: 'bulk-reconcile', label: 'Bulk Reconcile' },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setPageTab(t.key)}
              className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
                pageTab === t.key
                  ? 'border-blue-400 text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {pageTab === 'companies' && (
          <>
            {/* Search */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search companies..."
                  className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600 transition-colors"
                />
              </div>
              <span className="text-xs text-gray-600 ml-auto">
                {companies.length} of {totalElements}
                {totalPages > 1 && ` · Page ${page + 1}/${totalPages}`}
              </span>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
              </div>
            ) : (
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="px-3 py-3 w-8">
                        <input
                          type="checkbox"
                          checked={companies.length > 0 && companies.every(c => selectedRfIds.includes(c.rf_id))}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                        />
                      </th>
                      <th className="px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-left">Company</th>
                      <th className="px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-left">GL Status</th>
                      <th className="px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right">Rows</th>
                      <th className="px-5 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-left">Last Synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company, i) => (
                      <tr
                        key={company.rf_id}
                        onClick={() => setSelectedCompany(company)}
                        className={`border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors cursor-pointer ${selectedRfIds.includes(company.rf_id) ? 'bg-blue-500/5' : i % 2 === 0 ? '' : 'bg-gray-900/30'}`}
                      >
                        <td className="px-3 py-3.5" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedRfIds.includes(company.rf_id)}
                            onChange={() => toggleSelect(company.rf_id)}
                            className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
                          />
                        </td>
                        <td className="px-5 py-3.5">
                          <div>
                            <div className="text-sm font-medium text-gray-200">{company.display_name || company.rf_id}</div>
                            <div className="text-[11px] text-gray-600 font-mono mt-0.5">{company.rf_id}</div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={company.last_synced_at && company.row_count > 0 ? 'COMPLETED' : 'NEVER_SYNCED'} />
                        </td>
                        <td className="px-5 py-3.5 text-right text-sm text-gray-300 font-mono tabular-nums">
                          {company.row_count > 0 ? company.row_count.toLocaleString() : <span className="text-gray-600">—</span>}
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-400">
                          {company.last_synced_at ? formatDate(company.last_synced_at) : <span className="text-gray-600">Never</span>}
                        </td>
                      </tr>
                    ))}
                    {companies.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-16 text-center text-gray-600 text-sm">
                          {search ? 'No companies match your search' : 'No companies found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-gray-500">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalElements)} of {totalElements}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(0)}
                    disabled={page === 0}
                    className="px-2.5 py-1 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded disabled:opacity-30 transition-all"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-2.5 py-1 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded disabled:opacity-30 transition-all"
                  >
                    Prev
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i;
                    else if (page < 3) pageNum = i;
                    else if (page > totalPages - 4) pageNum = totalPages - 5 + i;
                    else pageNum = page - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-2.5 py-1 text-xs rounded transition-all ${
                          page === pageNum
                            ? 'bg-gray-700 text-white'
                            : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-2.5 py-1 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded disabled:opacity-30 transition-all"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(totalPages - 1)}
                    disabled={page >= totalPages - 1}
                    className="px-2.5 py-1 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded disabled:opacity-30 transition-all"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {pageTab === 'bulk-reconcile' && (
          <BulkReconcileTab
            companies={companies}
            onSelectCompany={(rfId) => {
              const company = companies.find(c => c.rf_id === rfId);
              if (company) {
                setSelectedCompany(company);
                setPageTab('companies');
              }
            }}
          />
        )}
      </div>

      {/* Company Drawer */}
      {selectedCompany && (
        <CompanyDrawer company={selectedCompany} onClose={() => setSelectedCompany(null)}>
          {(tab) => {
            if (tab === 'gl') return <GLSyncTab company={selectedCompany} onSyncComplete={fetchData} />;
            if (tab === 'entity') return <EntitySyncTab company={selectedCompany} />;
            if (tab === 'reconcile') return <ReconcileTab company={selectedCompany} />;
            return null;
          }}
        </CompanyDrawer>
      )}
    </div>
  );
}
